
import { blobToJson, parseIntArray, findValueOffsets, subBlobToJson, subBlobToJsonInt } from '../common/StringUtils';
import { binarySearch } from '../common/SearchUtils';

import { AttributeType, AttributeFlags, RVT_DIM_PROPS_BLACKLISTED, IFC_NWD_ITEM_PROPS_BLACKLISTED } from './PropdbEnums';
import { appendSuffixToName, getSuffixValue, SUFFIX_ATTRIBUTE_MAP } from './PropdbNameSuffix';

//Inlined into enumObjectProperties below
/*
function readVarint(buf, offset) {
    var b;
    var value = 0;
    var shiftBy = 0;
    do {
        b = buf[offset[0]++];
        value |= (b & 0x7f) << shiftBy;
        shiftBy += 7;
    } while (b & 0x80);
    return value;
}
*/

/**
 * The Property Database contains property information for each part of a model.
 * The data is read-only, since it has been packed to optimize memory footprint.
 * It's implemented as an Entity-Atribute-Value (EAV) set of tables.
 * LMV keeps the PropertyDatabase in a browser worker thread to prevent compute-intensive
 * methods to block the main browser UI thread.
 * Words "Attribute" and "Property" are use interchangeably.
 *
 * @class
 * @alias Autodesk.Viewing.Private.PropertyDatabase
 */
export class PropertyDatabase {

  _isV2 = false;
  _isVarint = false;

  //The property db json arrays.
  //Some of them are held unparsed in blob form
  //with helper arrays containing offsets into the blobs for each value to be parsed on demand
  /** @type {Array.<Array.<*>>} Array of arrays. Inner array is in the form [attrName(0), category(1), dataType(2), dataTypeContext(3), description(4), displayName(5), flags(6), precision(7) ] */
  _attrs = null;
  // See struct AttributeDef in https://git.autodesk.com/A360/platform-translation-propertydb/blob/master/propertydb/PropertyDatabase.h
  _offsets = null;
  _avs = null;
  _valuesBlob = null;
  _valuesOffsets = null;
  _idsBlob = null;
  _idsOffsets = null;
  _byteCount = 0;

  //Cached ids of commonly used well known attributes (child, parent, name)
  _childAttrId;
  _parentAttrId;
  _nameAttrId;
  _instanceOfAttrId;
  _viewableInAttrId;
  _externalRefAttrId;
  _nodeFlagsAttrId;
  _layersAttrId;
  _categoryIdAttrId;
  _elementIdAttrId;
  _nameSuffixAttrId;

  //Transient structures for detecting invalid cycles in buildObjectTree()
  _processedIds = null;
  _cyclesCount = 0;

  // Used for memoizing recursive calls to getNodeNameAndChildren
  _instanceNodeCache = {};

  // Cache for instanceof inheritance
  _instanceOfCache = {};

  // File type for applying file-specific naming logic
  _fileType = null;

  /**
   *
   * @param {object} dbjsons is expected to be of the form {{ attrs: {filename1:x, filename2:y}, ids: {filename1:x... }, values: {... }, offsets: {... }, avs: {... } }
   *                where each of the elements of each array is a pair of the original name and the unzipped *raw*    byte
   *                array buffer corresponding to the respective property database constituent. In the current implementation
   *                each array is expected to only have one name-value element.
   * @param {string} fileType Optional file type ('rvt', 'dwg', etc.) for applying file-specific naming logic
   */
  constructor(dbjsons, fileType) {

    this._fileType = fileType || null;

    //=========================================================================

    for (var p in dbjsons.attrs) {

      this._attrs = [];

      this._attrs = this._parseAttributesSafe(dbjsons.attrs[p]);

      this._byteCount += dbjsons.attrs[p].byteLength;

      if (this._attrs[0] === "pdb version 2")
      this._isV2 = true;

      // Cache the attribute ID for the name suffix based on file type
      const suffixAttrConfig = this._isV2 && this._fileType && SUFFIX_ATTRIBUTE_MAP[this._fileType] || null;

      for (var i = 1; i < this._attrs.length; i++) {

        var attrName = this._attrs[i][0];
        switch (attrName) {
          case "Layer":this._layersAttrId = i;break;
          case "ElementId":this._elementIdAttrId = i;break;
          default:break;
        }

        var category = this._attrs[i][1];

        switch (category) {
          case "__parent__":this._parentAttrId = i;break;
          case "__child__":this._childAttrId = i;break;
          case "__name__":this._nameAttrId = i;break;
          case "__instanceof__":this._instanceOfAttrId = i;break;
          case "__viewable_in__":this._viewableInAttrId = i;break;
          case "__externalref__":this._externalRefAttrId = i;break;
          case "__node_flags__":this._nodeFlagsAttrId = i;break;
          case "__categoryId__":this._categoryIdAttrId = i;break;
          default:break;
        }

        //As of V2, DbKey attribute values are stored directly into the AV array
        if (this._isV2 && this._attrs[i][2] === AttributeType.DbKey) {
          this._attrs[i][6] = this._attrs[i][6] | AttributeFlags.afDirectStorage;
        }

        // Check for name suffix attribute match
        if (suffixAttrConfig && !this._nameSuffixAttrId) {
          const nameMatches = attrName === suffixAttrConfig.name;
          const categoryMatches = category == suffixAttrConfig.category; //Allowed to be empty stirng or null
          if (nameMatches && categoryMatches) {
            this._nameSuffixAttrId = i;
          }
        }
      }

      break; //currently we can only handle single property file (no chunking)
    }

    //manual parse of the attribute-value index pairs array
    for (let p in dbjsons.avs) {

      let buf = dbjsons.avs[p];

      if (buf[0] === "[".charCodeAt(0)) {
        this._avs = parseIntArray(dbjsons.avs[p], 0);
      } else {
        this._avs = buf;
        this._isVarint = true;
      }

      delete dbjsons.avs; //don't need this blob anymore

      break; //currently we can only handle single property file (no chunking)

    }


    //manual parse of the offsets array
    for (let p in dbjsons.offsets) {

      let buf = dbjsons.offsets[p];

      if (buf[0] === "[".charCodeAt(0)) {
        this._offsets = parseIntArray(buf, 1); //passing in 1 to reserve a spot for the sentinel value

        //just a sentinel value to make lookups for the last item easier
        this._offsets[this._offsets.length - 1] = this._avs.length / 2;
      } else {
        this._offsets = new Int32Array(buf.buffer, buf.byteOffset, buf.byteLength / 4);
      }

      delete dbjsons.offsets; //don't need this

      break; //currently we can only handle single property file (no chunking)

    }

    //Instead of parsing the values and ids arrays, find the
    //offset of each json item in the blob, and then we can
    //pick and parse specific items later on demand, without
    //parsing the potentially large json blob up front.
    for (let p in dbjsons.values) {
      this._valuesBlob = dbjsons.values[p];
      this._valuesOffsets = findValueOffsets(this._valuesBlob);

      break; //currently we can only handle single property file (no chunking)

    }

    // Get externalIds from data blob. Unlike the other data, this one is optional
    // and may be loaded later or not at all to save memory.
    //Set ids array (if available). Note that the ids array is
    //optional and LMV does not require them. (Just some extensions might do - like PushPins)
    for (let p in dbjsons.ids) {
      this.setIdsBlob(dbjsons.ids[p]);
      break; //currently we can only handle single property file (no chunking)
    }
  }

  _parseAttributesSafe(buffer) {
    if (buffer.byteLength < Math.pow(2, 23)) {
      return blobToJson(buffer);
    } else {
      const result = [];
      const offsets = findValueOffsets(buffer);
      for (let i = 0; i < offsets.length; i++) {
        const attr = subBlobToJson(buffer, offsets[i]);
        result.push(attr);
      }
      return result;
    }
  }

  /**
   * Set externalIds blob
   * @param {Uint8Array} data
   */
  setIdsBlob(data) {
    // Just like for _valuesBlob: Find the offset to each value
    //  but skip the full parse.
    this._idsBlob = data;
    this._idsOffsets = findValueOffsets(data);
  }

  //=========================================================================

  /**
   * Obtains the number of database ids (dbIds) available.
   * These ids range betwee 1 (inclusive) up to getObjectCount() (exclusive).
   *
   * @returns {number}
   * @alias Autodesk.Viewing.Private.PropertyDatabase#getObjectCount
   */
  getObjectCount() {
    return this._offsets.length - 1;
  }

  isNullish(aid, vid) {
    const rawAttr = this._attrs[aid];
    if (rawAttr && rawAttr & AttributeFlags.afDirectStorage) {
      return false; // Direct storage attributes will always have a non-null value ID
    }
    return vid === 0;
  }

  getValueAt(valId) {
    return subBlobToJson(this._valuesBlob, this._valuesOffsets[valId]);
  }

  //faster variant used for traversing the object hierarchy where
  //we know the data type of the value to be an integer
  getIntValueAt(valId) {
    return subBlobToJsonInt(this._valuesBlob, this._valuesOffsets[valId]);
  }

  getIdAt(entId) {
    return subBlobToJson(this._idsBlob, this._idsOffsets[entId]);
  }

  // Loading the (large) externalIDs can be skipped to save memory.
  externalIdsLoaded() {
    return Boolean(this._idsBlob);
  }

  /**
   * Obtains the actual value of a property.
   *
   * @param {number} attrId - The attribute id
   * @param {number} valId - The value id
   * @param {boolean} [integerHint] - If true the return value will be casted to integer.
   *
   * @returns {*}
   * @alias Autodesk.Viewing.Private.PropertyDatabase#getAttrValue
   */
  getAttrValue(attrId, valId, integerHint) {
    var attr = this._attrs[attrId];
    if (attr[6] & AttributeFlags.afDirectStorage) {
      if (attr[2] === AttributeType.DbKey) {
        //db keys are stored directly in the EAV triplet
        return valId;
      }
    }

    return integerHint ? this.getIntValueAt(valId) : this.getValueAt(valId);
  }

  _getObjectProperty(attrId, valId) {
    var attr = this._attrs[attrId];
    var displayName = attr[5] ? attr[5] : attr[0];
    var hidden = this.attributeHidden(attrId);

    // type values match those in PropertyDatabase.h
    // See: https://git.autodesk.com/A360/platform-translation-propertydb/blob/master/propertydb/PropertyDatabase.h#L67
    return {
      displayName: displayName,
      displayValue: this.getAttrValue(attrId, valId),
      displayCategory: attr[1],
      attributeName: attr[0],
      type: attr[2],
      units: attr[3],
      hidden: hidden,
      precision: attr[7] || 0
    };
  }

  /**
   * Obtains all properties for a given database id.
   *
   * @param {number} dbId - The database id
   * @param {string[]} [propFilter=null] - Array of property names to return values for. Use null for no filtering.
   * @param {boolean} [ignoreHidden=false] - true to ignore hidden properties.
   * @param {string[]} [propIgnored=null] - Array of property names to not include in the return value.
   *
   * @returns {object} consisting of attributes `name`, `dbId`, `properties` and `externalId`.
   * @alias Autodesk.Viewing.Private.PropertyDatabase#getObjectProperties
   */
  getObjectProperties(dbId, propFilter, ignoreHidden, propIgnored, categoryFilter) {
    var result = {
      "dbId": dbId,
      "properties": []
    };

    var needName = false;
    var nameSuffixValId;

    // Check if externalIds are wanted and available
    var filterContainsId = propFilter && propFilter.indexOf("externalId") !== -1;
    var idsLoaded = this.externalIdsLoaded();

    // If externalIds are explicitly addressed in the filter, we can assume that the caller expects
    // to have them in the result. So we should report an error if they are not loaded.
    if (filterContainsId && !idsLoaded) {
      console.error('Requesting externalID requires loading of the externalID table');
    }

    // We add externalIds to the result if...
    //  1. externalIds are in memory
    //  2. Either the filter includes externalIds or there is no filter
    var addExternalId = idsLoaded && (!propFilter || filterContainsId);
    if (addExternalId) {
      result.externalId = this.getIdAt(dbId);

      // If there are no other properties required, then just return.
      // Useful when we only care about fetching externalId-only data.
      if (propFilter && propFilter.length === 1) {
        return result;
      }
    }

    var parentProps = null;

    //Loop over the attribute index - value index pairs for the objects
    //and for each one look up the attribute and the value in their
    //respective arrays.
    this.enumObjectProperties(dbId, (attrId, valId) => {
      if (attrId === this._instanceOfAttrId) {
        //Recursively resolve any common properties from the parent of this instance
        //NOTE: Here we explicitly ignore hidden properties, because we don't
        //want the parent instance to override parent/child nodes and other structural
        //attributes. Specifically, Revit extraction has a bug where the model tree parent is
        //also instance prototype for its children, so we need to prevent the child
        //from gaining all its siblings as children of its own due to this inheritance.
        var res = this.getObjectProperties(this.getAttrValue(attrId, valId), propFilter, true /*ignoreHidden*/, propIgnored, categoryFilter);
        if (res && res.properties) {
          parentProps = res;
        }
        return;
      }

      var attr = this._attrs[attrId];

      if (propFilter && propFilter.indexOf(attr[0]) === -1 && propFilter.indexOf(attr[5]) === -1)
      return;

      if (categoryFilter && categoryFilter.indexOf(attr[1]) === -1)
      return;

      if (propIgnored && (propIgnored.indexOf(attr[0]) > -1 || propIgnored.indexOf(attr[5]) > -1))
      return;

      if (attrId === this._nameAttrId) {
        var val = this.getAttrValue(attrId, valId);
        needName = true;
        result.name = val;
      } else
      if (attrId === this._nameSuffixAttrId) {
        nameSuffixValId = valId;
      } else
      {

        //skip structural attributes, we don't want those to display
        //NOTE: The list of structural attributes that we check explicitly is not marked
        //as hidden in older versions of the property database, so if we ever want to
        //add them to the result list, we have to explicitly set the hidden flag for those.
        var hidden = this.attributeHidden(attrId);
        if (ignoreHidden && hidden) {
          return;
        }

        var prop = this._getObjectProperty(attrId, valId);
        result.properties.push(prop);
      }
    });

    //Combine instance properties with any parent object properties
    if (parentProps) {
      var myProps = {};
      var rp = result.properties;
      for (let i = 0; i < rp.length; i++) {
        myProps[rp[i].displayName] = 1;
      }

      if (!result.name)
      result.name = parentProps.name;

      var pp = parentProps.properties;
      for (let i = 0; i < pp.length; i++) {
        if (!Object.prototype.hasOwnProperty.call(myProps, pp[i].displayName)) {
          rp.push(pp[i]);
        }
      }
    }

    // Apply file-type-specific name suffix if applicable
    if (result.name && this._fileType) {
      const suffix = getSuffixValue(this, dbId, this._nameSuffixAttrId, nameSuffixValId, this._fileType, result.externalId);
      if (suffix) {
        result.name = appendSuffixToName(result.name, suffix);
      }
    }

    if (categoryFilter && !result.properties.length)
    return null;

    if (propFilter && !result.properties.length && !addExternalId && !needName)
    return null;

    return result;
  }

  /**
   * Obtains a map between each database id (dbId) and their corresponding external-id.
   * The external-id is the identifier used by the source file.
   * Example: A translated Revit file has a wall with dbId=1, but in Revit (desktop application) the identifier of that wall is "Wall-06-some-guid-here".
   *
   * @param {number[]} [extIdFilter] - Limits the result to only contain the ids in this array.
   *
   * @returns {object} map from dbId into external-id.
   * @alias Autodesk.Viewing.Private.PropertyDatabase#getExternalIdMapping
   */
  getExternalIdMapping(extIdFilter) {
    var mapping = {};
    if (this._idsOffsets && 'length' in this._idsOffsets) {// Check that it's an indexable type
      for (var dbId = 1, len = this._idsOffsets.length; dbId < len; ++dbId) {
        var externalId = this.getIdAt(dbId);
        if (!extIdFilter || extIdFilter[externalId] === true) {
          mapping[externalId] = dbId;
        }
      }
    }
    return mapping;
  }

  /**
   * Heuristically find the root node(s) of a scene
   * A root is a node that has children, has no (or null) parent and has a name.
   * There can be multiple nodes at the top level (e.g. Revit DWF), which is why
   * we should get the scene root with absolute certainty from the SVF instance tree,
   * but we would have to uncompress and parse that in -- something that is
   * not currently done. This is good enough for now (if pretty slow).
   * @return {Array.<*>}
   */
  findRootNodes() {
    var idroots = [];
    var idDetachedNodes = []; // These are nodes without a parent or child

    this.enumObjects((id) => {
      var hasChild = false;
      var hasParent = false;
      var hasName = false;

      this.enumObjectProperties(id, (attrId, valId) => {
        if (attrId === this._parentAttrId) {
          if (this.getAttrValue(attrId, valId, true)) //checks for null or zero parent id, in which case it's considered non-parent
            hasParent = true;
        } else if (attrId === this._childAttrId) {
          hasChild = true;
        } else
        if (attrId === this._nameAttrId) {
          hasName = true;
        }
      });

      if (hasName && !hasParent) {
        hasChild ? idroots.push(id) : idDetachedNodes.push(id);
      }
    });

    // Some dwfx files can have only detached nodes, in which case, we treat them as roots
    return idroots.length > 0 ? idroots : idDetachedNodes;
  }

  /**
   * Does the node have atleast one child?
   * @param {number} dbId
   * @return {boolean}
   */
  nodeHasChild(dbId) {
    let hasChild = false;

    this.enumObjectProperties(dbId, (attrId) => {
      if (attrId === this._childAttrId) {
        hasChild = true;
        return true; // break from enumerating
      }
    });

    return hasChild;
  }


  /**
   * Gets the immediate children of a node with the given dbId
   * @param {Object} node
   * @param {number} node.dbId
   * @param {string} [node.name]
   * @param {boolean} skipChildren
   */
  getNodeNameAndChildren(node /* {dbId:X, name:""} */, skipChildren) {
    var id = node.dbId;

    var children;
    var instanceOfValId;
    var nameSuffixValId;

    this.enumObjectProperties(id, (attrId, valId) => {
      var val;

      if (attrId === this._parentAttrId) {

        //node.parent = this.getAttrValue(attrId, valId, true); //eventually we will needs this instead of setting parent pointer when creating children below.
      } else if (attrId === this._childAttrId && !skipChildren) {val = this.getAttrValue(attrId, valId, true);

        // avoid parent->child links to the same dbId
        if (val !== node.dbId) {
          var child = { dbId: val, parent: node.dbId };
          if (!children)
          children = [child];else

          children.push(child);
        }

      } else if (attrId === this._nameAttrId) {
        node.name = this.getAttrValue(attrId, valId); //name is necessary for GUI purposes, so add it to the node object explicitly
      } else if (attrId === this._nodeFlagsAttrId) {
        node.flags = this.getAttrValue(attrId, valId, true); //flags are necessary for GUI/selection purposes, so add them to the node object
      } else if (attrId === this._instanceOfAttrId) {
        instanceOfValId = valId;
      } else if (attrId === this._nameSuffixAttrId) {
        nameSuffixValId = valId;
      }
    });

    //If this is an instance of another object,
    //try to get the object name/flags from there.
    //This is not done in the main loop above for performance reasons,
    //we only want to do the expensive thing of going up the object hierarchy
    //if the node does not actually have a name/flags attributes.
    if ((!node.name || !node.flags) && instanceOfValId) {
      const dbIdOfInstance = this.getAttrValue(this._instanceOfAttrId, instanceOfValId, true);

      // Check if the instance node already exists in cache.
      let tmp = this._instanceNodeCache[dbIdOfInstance];

      if (!tmp) {
        // Fetch instance node and memoize it.
        this._instanceNodeCache[dbIdOfInstance] = tmp = { dbId: dbIdOfInstance, name: null, flags: null };
        this.getNodeNameAndChildren(tmp, true);
      }

      //Take the name from the prototype object if the instance doesn't have it
      if (tmp.name && !node.name)
      node.name = tmp.name;

      //Take the node flags from the prototype object if the instance doesn't have it
      if (typeof node.flags !== "number" && typeof tmp.flags === "number")
      node.flags = tmp.flags;
    }

    // Apply file-type-specific name suffix if applicable
    if (node.name && this._fileType) {
      const suffix = getSuffixValue(this, id, this._nameSuffixAttrId, nameSuffixValId, this._fileType, undefined);
      if (suffix) {
        node.name = appendSuffixToName(node.name, suffix);
      }
    }

    return children;
  }

  //Duplicated from InstanceTree.js
  static NODE_TYPE_ASSEMBLY = 0x0; // Real world object as assembly of sub-objects
  static NODE_TYPE_GEOMETRY = 0x6; // Leaf geometry node

  //Builds a tree of nodes according to the parent/child hierarchy
  //stored in the property database, starting at the node with the given dbId
  buildObjectTree(rootId, //current node dbId
  fragToDbId, //array of fragId->dbId lookup
  maxDepth, /* returns max tree depth */
  nodeStorage)
  {

    //Build reverse lookup for dbId->fragId
    var dbToFragId;
    if (fragToDbId) {
      dbToFragId = buildDbIdToFragMap(fragToDbId);
    }

    this._processedIds = {};
    this._cyclesCount = 0;

    //Call recursive implementation
    var ret = this.buildObjectTreeRec(rootId, 0, dbToFragId, 0, maxDepth, nodeStorage);
    if (this._cyclesCount > 0) {
      console.warn('Property database integrity not guaranteed (' + this._cyclesCount + ').');
    }

    this._processedIds = null;

    // Clean cache
    this._instanceNodeCache = {};

    return ret;
  }

  //Recursive helper for buildObjectTree
  buildObjectTreeRec(dbId, //current node dbId
  parent, //parent dbId
  dbToFrag, //map of dbId to fragmentIds
  depth, /* start at 0 */
  maxDepth, /* returns max tree depth */
  nodeStorage)
  {

    // Check for cycles in the tree.
    // There shouldn't be any cycles in the tree...
    if (this._processedIds[dbId]) {
      this._cyclesCount++;
      return 0;
    }

    this._processedIds[dbId] = parent || dbId;

    if (depth > maxDepth[0])
    maxDepth[0] = depth;

    var node = { dbId: dbId };
    var children = this.getNodeNameAndChildren(node);

    var childrenIds = [];

    if (children) {
      for (var j = 0; j < children.length; j++) {
        var childHasChildren = this.buildObjectTreeRec(children[j].dbId, dbId, dbToFrag, depth + 1, maxDepth, nodeStorage);

        //For display purposes, prune children that are leafs without graphics
        //and add the rest to the node
        if (childHasChildren)
        childrenIds.push(children[j].dbId);
      }
    }

    var fragIds;

    //leaf node
    if (dbToFrag) {
      var frags = dbToFrag[dbId];
      if (frags !== undefined) {

        //if (childrenIds.length)
        //    console.error("Node that has both node children and fragment children!", node.name, children, childrenIds, frags);

        if (!Array.isArray(frags))
        fragIds = [frags];else

        fragIds = frags;
      }
    }

    //Use default node flags in case none are set
    //This is not the best place to do this, but it's
    //the last place where we can differentiate between "not set" and zero.
    let flags = node.flags;
    if (flags === undefined) {
      if (fragIds && fragIds.length)
      flags = PropertyDatabase.NODE_TYPE_GEOMETRY;else
      if (childrenIds.length)
      flags = PropertyDatabase.NODE_TYPE_ASSEMBLY;else

      flags = 0; //??? Should not happen (those nodes are pruned above)
    }

    // Get child count (nodes + fragments)
    var childCount = childrenIds.length + (fragIds ? fragIds.length : 0);

    // Skip nodes that contain neither children nor any fragments
    if (childCount) {
      nodeStorage.setNode(dbId, parent, node.name, flags, childrenIds, fragIds, depth);
    }

    return childCount;
  }

  /**
   * Given a text string, returns an array of individual words separated by
   * white spaces.
   * Will preserve white spacing within double quotes.
   *
   * @param {string} searchText - Text to search
   * @alias Autodesk.Viewing.Private.PropertyDatabase#getSearchTerms
   */
  getSearchTerms(searchText) {
    searchText = searchText.toLowerCase();
    //regex preserves double-quote delimited strings as phrases
    var searchTerms = searchText.match(/"[^"]+"|[^\s]+/g) || [];
    var i = searchTerms.length;
    while (i--) {
      searchTerms[i] = searchTerms[i].replace(/"/g, "");
    }
    var searchList = [];
    for (i = 0; i < searchTerms.length; i++) {

      if (searchTerms[i].length > 1)
      searchList.push(searchTerms[i]);
    }
    return searchList;
  }

  /**
   * Searches the property database for a string.
   *
   * @param searchText
   * @param attributeNames
   * @param searchOptions
   * @returns Array of ids.
   * @private
   */
  bruteForceSearch(searchText, attributeNames, searchOptions) {
    let searchList;

    if (searchOptions?._privUseRegex && searchText.startsWith("/")) {
      //Parses out the RegExp and its flags from an input of the form /expression/flags
      //e.g. /^Wall/i
      const match = searchText.match(/^\/(.*)\/([gimuy]*)$/);
      searchList = match ? [new RegExp(match[1], match[2])] : this.getSearchTerms(searchText);
    } else {
      searchList = this.getSearchTerms(searchText);
    }

    if (searchList.length === 0)
    return [];

    //For each search word, find matching IDs
    const results = [];

    const searchHidden = searchOptions?.searchHidden;
    const includeInherited = searchOptions?.includeInherited;
    // { x: [ a, b, c ]}, a,b,c are instances of x
    const inheritanceMap = {};

    for (let k = 0; k < searchList.length; k++) {

      const result = [];

      const searchTerm = searchList[k];

      //Find all values that match the search text
      let matching_vals = [];
      if (typeof searchTerm === "string") {
        for (let i = 0, iEnd = this._valuesOffsets.length; i < iEnd; i++) {
          let val = this.getValueAt(i);
          if (val === null)
          continue;
          if (val.toString().toLowerCase().indexOf(searchList[k]) !== -1)
          matching_vals.push(i);
        }
      } else {
        for (let i = 0, iEnd = this._valuesOffsets.length; i < iEnd; i++) {
          let val = this.getValueAt(i);
          if (val === null)
          continue;
          if (searchTerm.test(val.toString()))
          matching_vals.push(i);
        }
      }

      if (matching_vals.length === 0) {
        results.push(result);
        continue;
      }

      // values should be sorted at this point, but it doesn't hurt making sure they are.
      matching_vals.sort(function (a, b) {
        return a - b;
      });

      this.enumObjects((id) => {

        this.enumObjectProperties(id, (attrId, valId) => {

          if (includeInherited && attrId === this._instanceOfAttrId) {
            const pid = parseInt(this.getAttrValue(attrId, valId));
            inheritanceMap[pid] = inheritanceMap[pid] || [];
            inheritanceMap[pid].push(id);
          }

          if (!searchHidden) {
            // skip hidden attributes
            const isHidden = this.attributeHidden(attrId);
            if (isHidden) {
              return;
            }
          }

          const iFound = binarySearch(matching_vals, valId);
          if (iFound !== -1) {
            //Check attribute name in case a restriction is passed in
            if (attributeNames && attributeNames.length && attributeNames.indexOf(this._attrs[attrId][0]) === -1)
            return;

            result.push(id);
            return true;
          }
        });

      });

      results.push(result);
    }

    const addInheritedIds = (ids, inheritanceMap) => {
      // traverse inheritance and add to ids list
      const seen = new Set(ids);
      for (let i = 0; i < ids.length; ++i) {
        const id = ids[i];
        if (!(id in inheritanceMap)) continue;
        const cids = inheritanceMap[id];
        for (let j = 0; j < cids.length; ++j) {
          const cid = cids[j];
          if (!seen.has(cid)) {
            ids.push(cid);
            seen.add(cid);
          }
        }
      }
    };

    if (results.length === 1) {
      if (includeInherited) {
        addInheritedIds(results[0], inheritanceMap);
      }
      return results[0];
    }

    //If each search term resulted in hits, compute the intersection of the sets
    let map = {};
    let hits = results[0];
    for (let i = 0; i < hits.length; i++)
    map[hits[i]] = 1;


    for (let j = 1; j < results.length; j++) {
      hits = results[j];
      const mapint = {};

      for (let i = 0; i < hits.length; i++) {
        if (map[hits[i]] === 1)
        mapint[hits[i]] = 1;
      }

      map = mapint;
    }

    const result = [];
    for (let k in map) {
      result.push(parseInt(k));
    }

    if (includeInherited) {
      addInheritedIds(result, inheritanceMap);
    }

    return result;
  }

  /**
   * Given a property name, it returns an array of ids that contain it.
   *
   * @param {string} propertyName - Property name
   * @alias Autodesk.Viewing.Private.PropertyDatabase#bruteForceFind
   */
  bruteForceFind(propertyName) {
    var results = [];
    this.enumObjects((id) => {

      var idContainsProperty = false;
      this.enumObjectProperties(id, (attrId) => {

        var attr = this._attrs[attrId];
        var propName = attr[0];
        var displayName = attr[5];

        if (propName === propertyName || displayName === propertyName) {
          idContainsProperty = true;
          return true;
        }
      });

      if (idContainsProperty) {
        results.push(id);
      }

    });

    return results;
  }

  /**
   * Specialized function that returns:
   * {
   *    'layer-name-1': [id1, id2, ..., idN],
   *    'layer-name-2': [idX, idY, ..., idZ],
   *    ...
   * }
   * @alias Autodesk.Viewing.Private.PropertyDatabase#getLayerToNodeIdMapping
   */
  getLayerToNodeIdMapping() {
    var results = {};
    this.enumObjects((id) => {

      this.enumObjectProperties(id, (attrId, valId) => {

        if (attrId !== this._layersAttrId)
        return;

        var val = this.getAttrValue(attrId, valId);
        if (!Array.isArray(results[val])) {
          results[val] = [];
        }
        results[val].push(id);
        return true;
      });

    });

    return results;
  }

  /**
   * Unpacks an attribute value into all available components.
   *
   * @param {number} attrId - The attribute id.
   *
   * @returns {object} containing `name`, `category`, `dataType`, `dataTypeContext`, `description`, `displayName` and `flags`.
   * @alias Autodesk.Viewing.Private.PropertyDatabase#getAttributeDef
   */
  getAttributeDef(attrId) {
    var _raw = this._attrs[attrId];
    if (!_raw) return;
    return {
      //attrName(0), category(1), dataType(2), dataTypeContext(3), description(4), displayName(5), flags(6), precision(7)
      name: _raw[0],
      category: _raw[1],
      dataType: _raw[2],
      dataTypeContext: _raw[3],
      description: _raw[4],
      displayName: _raw[5],
      flags: _raw[6],
      precision: _raw.length > 7 ? _raw[7] : 0
    };
  }

  /**
   * Invokes a callback function for each attribute-id in the model.
   *
   * @example
   *      pdb.enumAttributes(function(attrId, attrDef) {
   *           // attrDef is an object
   *           if (attrDef.name === 'name') {
   *               return true; // return true to stop iteration.
   *           }
   *      })
   *
   * @param {Function} cb - Callback invoked
   * @alias Autodesk.Viewing.Private.PropertyDatabase#enumAttributes
   */
  enumAttributes(cb) {
    for (var i = 1; i < this._attrs.length; i++) {
      if (cb(i, this.getAttributeDef(i), this._attrs[i]))
      break;
    }
  }

  //See API doc for enumObjectProperties below
  _enumObjectPropertiesV1(dbId, cb) {
    //Start offset of this object's properties in the Attribute-Values table
    let propStart = 2 * this._offsets[dbId];

    //End offset of this object's properties in the Attribute-Values table
    let propEnd = 2 * this._offsets[dbId + 1];

    //Loop over the attribute index - value index pairs for the objects
    //and for each one look up the attribute and the value in their
    //respective arrays.
    for (let i = propStart; i < propEnd; i += 2) {
      let attrId = this._avs[i];
      let valId = this._avs[i + 1];

      if (cb(attrId, valId))
      break;
    }
  }

  //See API doc for enumObjectProperties below
  _enumObjectPropertiesV2(dbId, cb) {
    //v2 variable length encoding. Offsets point into delta+varint encoded a-v pairs per object
    let offset = this._offsets[dbId];
    let propEnd = this._offsets[dbId + 1];
    let buf = this._avs;

    let a = 0;
    while (offset < propEnd) {

      //Inlined version of readVarint
      let b = buf[offset++];
      let value = b & 0x7f;
      let shiftBy = 7;
      while (b & 0x80) {
        b = buf[offset++];
        value |= (b & 0x7f) << shiftBy;
        shiftBy += 7;
      }

      //attribute ID is delta encoded from the previously seen attribute ID, add that back in
      a += value;

      //Inlined version of readVarint
      b = buf[offset++];
      value = b & 0x7f;
      shiftBy = 7;
      while (b & 0x80) {
        b = buf[offset++];
        value |= (b & 0x7f) << shiftBy;
        shiftBy += 7;
      }

      if (cb(a, value))
      break;
    }
  }

  /**
   * Iterates over all properties for a given database id and invokes the supplied callback function.
   *
   * @param {number} dbId - The attribute id.
   * @param {Function} cb - callback function, that receives 2 arguments: attribute-id (`attrId`) and value-id (`valId`). Have the function return `true` to abort iteration.
   *
   * @alias Autodesk.Viewing.Private.PropertyDatabase#enumObjectProperties
   */
  enumObjectProperties(dbId, cb) {
    if (this._isVarint) {
      return this._enumObjectPropertiesV2(dbId, cb);
    } else {
      return this._enumObjectPropertiesV1(dbId, cb);
    }
  }

  //See API documentation in getPropertiesSubsetWithInheritance below
  _getPropertiesSubsetWithInheritanceV1(dbId, desiredAttrIds, dstValIds) {

    //Start offset of this object's properties in the Attribute-Values table
    let propStart = 2 * this._offsets[dbId];

    //End offset of this object's properties in the Attribute-Values table
    let propEnd = 2 * this._offsets[dbId + 1];

    let res = [];
    let instanceOfVals = [];
    dstValIds = dstValIds || {};

    //Loop over the attribute index - value index pairs for the objects
    //and for each one look up the attribute and the value in their
    //respective arrays.
    for (let i = propStart; i < propEnd; i += 2) {
      let a = this._avs[i];
      let value = this._avs[i + 1];

      if (a === this._instanceOfAttrId) {
        //remember instanceof inheritance for later
        let iofDbId = this.getAttrValue(a, value);
        instanceOfVals.push(iofDbId);
      } else {
        if (!desiredAttrIds || desiredAttrIds[a]) {
          dstValIds[a] = value;
          res.push(a);
          res.push(value);
        }
      }
    }

    //Really, we only expect one instanceof inheritance, but
    //it's theoretically possible to have several
    for (let i = 0; i < instanceOfVals.length; i++) {

      let iofDbId = instanceOfVals[i];

      let cached = this._instanceOfCache[iofDbId];
      if (!cached) {
        this._instanceOfCache[iofDbId] = cached = this._getPropertiesSubsetWithInheritanceV1(iofDbId);
      }

      for (let j = 0; j < cached.length; j += 2) {
        let a = cached[j];
        let v = cached[j + 1];

        if (desiredAttrIds && !desiredAttrIds[a]) {
          continue;
        }

        if (!dstValIds[a] && !this.attributeHidden(a)) {
          dstValIds[a] = v;
          res.push(a);
          res.push(v);
        }
      }
    }

    return res;
  }

  //See API documentation in getPropertiesSubsetWithInheritance below
  _getPropertiesSubsetWithInheritanceV2(dbId, desiredAttrIds, dstValIds) {

    //v2 variable length encoding. Offsets point into delta+varint encoded a-v pairs per object
    let offset = this._offsets[dbId];
    let propEnd = this._offsets[dbId + 1];
    let buf = this._avs;

    let res = [];
    let instanceOfVals = [];
    dstValIds = dstValIds || {};

    let a = 0;
    while (offset < propEnd) {

      let b = buf[offset++];
      let value = b & 0x7f;
      let shiftBy = 7;
      while (b & 0x80) {
        b = buf[offset++];
        value |= (b & 0x7f) << shiftBy;
        shiftBy += 7;
      }

      //attribute ID is delta encoded from the previously seen attribute ID, add that back in
      a += value;

      b = buf[offset++];
      value = b & 0x7f;
      shiftBy = 7;
      while (b & 0x80) {
        b = buf[offset++];
        value |= (b & 0x7f) << shiftBy;
        shiftBy += 7;
      }

      if (a === this._instanceOfAttrId) {
        //remember instanceof inheritance for later
        let iofDbId = this.getAttrValue(a, value);
        instanceOfVals.push(iofDbId);
      } else {
        if (!desiredAttrIds || desiredAttrIds[a]) {
          dstValIds[a] = value;
          res.push(a);
          res.push(value);
        }
      }
    }

    //Really, we only expect one instanceof inheritance, but
    //it's theoretically possible to have several
    for (let i = 0; i < instanceOfVals.length; i++) {

      let iofDbId = instanceOfVals[i];

      let cached = this._instanceOfCache[iofDbId];
      if (!cached) {
        this._instanceOfCache[iofDbId] = cached = this._getPropertiesSubsetWithInheritanceV2(iofDbId);
      }

      for (let j = 0; j < cached.length; j += 2) {
        let a = cached[j];
        let v = cached[j + 1];

        if (desiredAttrIds && !desiredAttrIds[a]) {
          continue;
        }

        if (!dstValIds[a] && !this.attributeHidden(a)) {
          dstValIds[a] = v;
          res.push(a);
          res.push(v);
        }
      }
    }

    return res;
  }

  /**
   * Given an object ID, returns the corresponding value IDs for the given list of attribute Ids.
   * Takes into account instance_of inheritance of properties.
   *
   * @param {number} dbId - Integer input object ID
   * @param {object} desiredAttrIds - An optional map of the requested attribute Ids, where desiredAttrIds[attrId] is "truthy".
   *                                  If not provided, all properties will be returned.
   * @param {object} dstValIds - A storage target map, such that dstValIds[attrId] will be the resulting value ID.
   *                             It is the responsibility of the caller to zero initialize this map.
   *
   * @returns {number[]} - A flat list of integers attributeId - valueId pairs. This is in addition to the dstValIds, for cases
   *                        where the object has mutliple properties of the same type, e.g. children, __viewable_in__, etc.
   * @alias Autodesk.Viewing.Private.PropertyDatabase#getPropertiesSubsetWithInheritance
   */
  getPropertiesSubsetWithInheritance(dbId, desiredAttrIds, dstValIds) {
    if (this._isVarint) {
      return this._getPropertiesSubsetWithInheritanceV2(dbId, desiredAttrIds, dstValIds);
    } else {
      return this._getPropertiesSubsetWithInheritanceV1(dbId, desiredAttrIds, dstValIds);
    }
  }

  /**
   * Iterates over the property database and finds all layers.
   *
   * @param {string} [viewScope] - Optional view-scope key. When provided and the database
   *   carries `__viewable_in__` relations, only layers reachable from entities tagged with
   *   this scope are returned. Pass the OTG graphics-node URN (matches the `m_viewUrn` ATF
   *   writes via `FindOrAddEntity(extId, scope)`). When the scope is unresolvable or
   *   `__viewable_in__` is absent, falls back to the unscoped enumeration so callers that
   *   pass no scope keep working.
   * @returns {object}
   * @alias Autodesk.Viewing.Private.PropertyDatabase#findLayers
   */
  findLayers(viewScope) {

    // Same format as F2d.js::createLayerGroups()
    var ret = { name: 'root', id: 1, index: 1, children: [], isLayer: false, childCount: 0 };

    // Return early when no Layer attribute is present
    if (this._layersAttrId === undefined) {
      return ret;
    }

    // Resolve the view-scope string to its valId once. -1 means "no filter" — either
    // the caller did not pass a scope, the PDB lacks a __viewable_in__ attribute, or
    // the scope string is not registered in the values blob. Falling through to the
    // unscoped path preserves backwards compatibility with those cases.
    var scopeValId = -1;
    if (viewScope && this._viewableInAttrId !== undefined && this._valuesOffsets) {
      for (var v = 0, vEnd = this._valuesOffsets.length; v < vEnd; v++) {
        if (this.getValueAt(v) === viewScope) {
          scopeValId = v;
          break;
        }
      }
    }

    // Grab all Layer names
    var layers = [];
    this.enumObjects((dbId) => {
      var layerName = null;
      var inScope = scopeValId === -1;
      this.enumObjectProperties(dbId, (attrId, valId) => {
        if (attrId === this._layersAttrId) {
          if (layerName === null) layerName = this.getValueAt(valId);
          // No scope filter: stop as soon as the layer name is known, matching
          // the original early-out. When scoping, keep scanning for __viewable_in__.
          if (scopeValId === -1) return true;
        } else if (attrId === this._viewableInAttrId && valId === scopeValId) {
          inScope = true;
        }
      });
      if (inScope && layerName && layers.indexOf(layerName) === -1) {
        layers.push(layerName);
      }
    });

    layers.sort(function (a, b) {
      return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
    });

    // Format output to match F2d.js::createLayerGroups()
    ret.childCount = layers.length;
    ret.children = layers.map(function (layerName, index) {
      return {
        name: layerName,
        index: index + 1,
        id: index + 1,
        isLayer: true
      };
    });

    return ret;
  }

  /**
   * Iterates over all database ids and invokes a callback function.
   *
   * @param {Function} cb - callback function. Receives a single parameter: the database-id. Have the function return true to abort iteration.
   * @param {number} fromId - starting id (inclusive)
   * @param {number} toId - end id (exclusive)
   *
   * @alias Autodesk.Viewing.Private.PropertyDatabase#enumObjects
   */
  enumObjects(cb, fromId, toId) {

    // For a given id, the range in _avs is specified by [offsets[id], _offsets[id+1]].
    // The last element in _offsets is just the range end of the final range.
    var idCount = this._offsets.length - 1; //== this.getObjectCount()

    if (typeof fromId === "number") {
      fromId = Math.max(fromId, 1);
    } else {
      fromId = 1;
    }

    if (typeof toId === "number") {
      toId = Math.min(idCount, toId);
    } else {
      toId = idCount;
    }

    for (var id = fromId; id < toId; id++) {
      if (cb(id))
      break;
    }
  }

  getAttrChild() {
    return this._childAttrId;
  }

  getAttrParent() {
    return this._parentAttrId;
  }

  getAttrName() {
    return this._nameAttrId;
  }

  getAttrLayers() {
    return this._layersAttrId;
  }

  getAttrInstanceOf() {
    return this._instanceOfAttrId;
  }

  getAttrViewableIn() {
    return this._viewableInAttrId;
  }

  getAttrXref() {
    return this._externalRefAttrId;
  }

  getAttrNodeFlags() {
    return this._nodeFlagsAttrId;
  }

  getAttrCategoryId() {
    return this._categoryIdAttrId;
  }

  getAttrElementId() {
    return this._elementIdAttrId;
  }

  /**
   * Checks whether an attribute is hidden or not.
   *
   * @param {number} attrId - The attribute id.
   *
   * @returns {boolean} - true if the attribute is a hidden one.
   * @alias Autodesk.Viewing.Private.PropertyDatabase#attributeHidden
   */
  attributeHidden(attrId) {
    var _raw = this._attrs[attrId];
    var flags = _raw[6];

    return flags & 1 /*afHidden*/ ||
    attrId === this._parentAttrId ||
    attrId === this._childAttrId ||
    attrId === this._viewableInAttrId ||
    attrId === this._externalRefAttrId;
  }

  _attributeIsBlacklisted(attrId) {
    var _raw = this._attrs[attrId];
    var name = _raw[0];
    var category = _raw[1];

    // IFC/NWD files contains a "Source File" property which is typically
    // different between the two versions and cause DiffTool to return noisy diff.
    // Ignore it for diff calculation.
    if (Object.prototype.hasOwnProperty.call(IFC_NWD_ITEM_PROPS_BLACKLISTED, category) && IFC_NWD_ITEM_PROPS_BLACKLISTED[category].indexOf(name) !== -1)
    return true;

    // In the IFC extractor v4, GlobalId may vary between versions but not have a fixed category. We can ignore it except for "IFC Attributes".
    if (name === "GlobalId" && category !== "IFC Attributes")
    return true;

    // Dimension properties were added at a later time, so
    // some diffs will fail when comparing a file with vs. file without those.
    // Anyway, it's better to skip those for diff purposes, since they can have numeric
    // noise and result in spurious diffs.
    // See also: https://git.autodesk.com/fluent/modeldb/commit/584d39d5f85a8d2954da557859bb52c224c402af
    if (Object.prototype.hasOwnProperty.call(RVT_DIM_PROPS_BLACKLISTED, category) && RVT_DIM_PROPS_BLACKLISTED[category].indexOf(name) !== -1)
    return true;

    return false;
  }

  /**
   * Checks whether an attribute must be excluded from Diff comparison.
   * @param {Object} propertyFilter - A key-value pair of category and property names to ignore from Diff
   * @param {number} attrId - The attribute id.
   * @internal
   */
  _ignoreAttribute(propertyFilter, attrId) {
    var _raw = this._attrs[attrId];
    var name = _raw[0];
    var category = _raw[1];

    return propertyFilter[category] && propertyFilter[category].has(name);
  }

  // Returns parent id of dbId or null if there is none.
  findParent(dbId) {
    let parent = null;
    this.enumObjectProperties(dbId, (attrId, valId) => {
      if (attrId === this._parentAttrId) {
        parent = this.getAttrValue(attrId, valId, true);
      }
    });
    return parent;
  }

  // Helper function for _findDifferences.
  // Finds all attributeIds and valueIds - including inherited ones.
  // Results are pushed to 'result' array as objects { attrId, valId }.
  // Hidden attributes are excluded.
  _getAttributeAndValueIds(dbId, result, propertyFilter) {

    let avList = this.getPropertiesSubsetWithInheritance(dbId);
    let ignorePropertyFlag = propertyFilter && Object.keys(propertyFilter).length >= 1;

    //Convert result to array and sort by attrId so diff can work.
    for (let i = 0; i < avList.length; i += 2) {
      let a = avList[i];

      if (this._attributeIsBlacklisted(a) || this.attributeHidden(a))
      continue;

      // Exclude user-specified properties from diff
      if (ignorePropertyFlag && this._ignoreAttribute(propertyFilter, a))
      continue;

      result.push({
        attrId: a,
        valId: avList[i + 1]
      });
    }

    result.sort(function byIncAtribId(a, b) {
      return a.attrId - b.attrId;
    });
  }

  /* Finds all common dbIds of this and another db for which the properties are not identical.
   * Hidden attributes are excluded.
   *  @param {PropertyDatabase} dbToCompare
   *  @param {Object} [DiffOptions] diffOptions
   *  @param {function(number)} [onProgress] gets progress values in percent
   *  @returns {Object}
   * See PropDbLoader.diffProperties for details about diffOptions and return value.
   *
   * NOTE: Current implementation only supports Otg models.
   *
   * @private
   */
  findDifferences(dbToCompare, diffOptions, onProgress) {

    var result = {
      changedIds: []
    };

    // Optional: Restrict search to the given ids
    var dbIds = diffOptions && diffOptions.dbIds;

    // Optional: Collect details about which props have changed
    var listPropChanges = diffOptions && diffOptions.listPropChanges;
    if (listPropChanges) {
      result.propChanges = [];
    }

    //Optional : Collect user-specified properties to ignore for diff
    let propertyFilter = diffOptions && diffOptions.propertyFilter || {};

    var db1 = this;
    var db2 = dbToCompare;

    // Reused array of { attrId, valId } pairs.
    var propIds1 = [];
    var propIds2 = [];

    // To support instanceOf attributes, we need to recursively check parent attributes/values too. (see getAttributesAndValues)
    // Since the parent of subsequent dbId is often the same, we cache the parent attribs/values that we got from last call.
    // This avoids to do the same check several times and can make diff significantly faster.
    var cachedParentAttribs1 = { result: [], dbId: -1 };
    var cachedParentAttribs2 = { result: [], dbId: -1 };

    var diffObject = (dbId) => {

      // get sorted array of {attrIds, valIds} pairs for both objects
      var i1 = 0;
      var i2 = 0;
      propIds1.length = 0;
      propIds2.length = 0;

      db1._getAttributeAndValueIds(dbId, propIds1, propertyFilter, true, cachedParentAttribs1);
      db2._getAttributeAndValueIds(dbId, propIds2, propertyFilter, true, cachedParentAttribs2);

      if (!propIds1.length || !propIds2.length) {
        // If an array is empty, this dbId does only exist
        // in one of the two dbs, i.e, the whole object was added or removed.
        // We are only interested in prop changes of matching objects.
        return;
      }

      var changeFound = false;

      // array of prop changes for current dbId
      var propChanges = undefined;

      while (i1 < propIds1.length && i2 < propIds2.length) {

        // Note that some values may be undefined if one of the arrays ended.
        var elem1 = propIds1[i1];
        var elem2 = propIds2[i2];
        var a1 = elem1 && elem1.attrId;
        var v1 = elem1 && elem1.valId;
        var a2 = elem2 && elem2.attrId;
        var v2 = elem2 && elem2.valId;

        // If everything is equal, we are done with this attribute
        if (a1 === a2 && v1 === v2) {
          i1++;
          i2++;
          continue;
        }

        // If we get here, the current attribute has changed
        changeFound = true;

        // If no details are requested, we are done with this dbId
        if (!listPropChanges) {
          break;
        }

        // We exploit here that attributeIds in OTG are always sorted in ascending order
        // Therefore, if a1 > a2, we can safely assume that a1 does not exist in iterator2,
        // but possibly vice versa.
        var prop1Missing = a1 === undefined || a1 > a2;
        var prop2Missing = a2 === undefined || a2 > a1;

        var change = undefined;

        // Handle case that property has been added or removed
        if (prop1Missing) {

          // property was added in db2
          change = db2._getObjectProperty(a2, v2);
          change.displayValueB = change.displayValue;
          change.displayValue = undefined;

          // a2 has been detected as added. Skip it and continue.
          i2++;
        } else if (prop2Missing) {

          // property was removed in db2
          change = db1._getObjectProperty(a1, v1);
          change.displayValueB = undefined;

          // a1 has been detected as removed. Skip it and continue.
          i1++;
        } else {
          // attrib exists in both, but value has changed
          change = db1._getObjectProperty(a1, v1);
          change.displayValueB = this.getAttrValue(a2, v2);

          i1++;
          i2++;
        }

        // If this is the first prop that change, alloc array for it
        if (!propChanges) {
          propChanges = [];
        }

        propChanges.push(change);
      }

      // Collect dbId of modified object
      if (changeFound) {
        result.changedIds.push(dbId);

        // collect correspondign prop change details
        if (listPropChanges) {
          result.propChanges.push(propChanges);
        }
      }
    };

    // Track progress
    var lastPercent = -1;
    var trackProgress = function (idsDone, idsTotal) {
      // Limit calls to 100 - otherwise it would slow-down everything.
      var percent = Math.floor(100 * idsDone / idsTotal);
      if (percent !== lastPercent) {
        onProgress && onProgress(percent);
        lastPercent = percent;
      }
    };

    if (dbIds) {
      // diff selected set of Ids
      for (var i = 0; i < dbIds.length; i++) {
        let dbId = dbIds[i];
        diffObject(dbId);
        trackProgress(i, dbIds.length);
      }
    } else {
      // diff all objects
      // Note: We are only searching for common objects that changed. Therefore, the loop
      //       runs only over dbIds that are within the valid range for both.
      var dbIdEnd = Math.min(db1.getObjectCount(), this.getObjectCount());
      for (let dbId = 1; dbId < dbIdEnd; dbId++) {
        diffObject(dbId);
        trackProgress(dbId, dbIdEnd);
      }
    }

    return result;
  }

  /**
   * Returns count of the number of attributes
   * @returns {number}
   * @alias Autodesk.Viewing.Private.PropertyDatabase#numberOfAttributes
   */
  numberOfAttributes() {
    return this._attrs?.length ?? 0;
  }

  /**
   * Returns count of the number of values
   * @returns {number}
   * @alias Autodesk.Viewing.Private.PropertyDatabase#numberOfValues
   */
  numberOfValues() {
    return this._valuesOffsets?.length ?? 0;
  }

  getMemoryUsage() {
    return this._byteCount + [this._offsets, this._avs, this._valuesBlob, this._valuesOffsets, this._idsBlob, this._idsOffsets].
    filter((it) => it).reduce((a, c) => a + c?.byteLength, 0);
  }

  dtor() {
    this._attrs = null;
    this._offsets = null;
    this._avs = null;
    this._valuesBlob = null;
    this._valuesOffsets = null;
    this._idsBlob = null;
    this._idsOffsets = null;

    this._childAttrId = 0;
    this._parentAttrId = 0;
    this._nameAttrId = 0;
    this._instanceOfAttrId = 0;
    this._viewableInAttrId = 0;
    this._externalRefAttrId = 0;
    this._nodeFlagsAttrId = 0;
    this._categoryIdAttrId = 0;
  }
}


/**
 * @param fragToDbId
 * @private
 */
export function buildDbIdToFragMap(fragToDbId) {
  let ret = {};
  for (var i = 0, iEnd = fragToDbId.length; i < iEnd; i++) {

    let dbIds = fragToDbId[i];

    //In 2D drawings, a single fragment (consolidation mesh)
    //can contain multiple objects with different dbIds.
    if (!Array.isArray(dbIds)) {
      dbIds = [dbIds];
    }

    for (var j = 0; j < dbIds.length; j++) {
      let dbId = dbIds[j];
      let frags = ret[dbId];
      if (frags === undefined) {
        //If it's the first fragments for this dbid,
        //store the index directly -- most common case.
        ret[dbId] = i;
      } else
      if (!Array.isArray(frags)) {
        //otherwise put the fragments that
        //reference the dbid into an array
        ret[dbId] = [frags, i];
      } else
      {
        //already is an array
        frags.push(i);
      }
    }
  }

  return ret;
}